/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IAdminClozeData } from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import Chip from '@design-system/chip';

interface IProps {
  cloze: IAdminClozeData;
  isSelected: boolean;
  handleClick: () => void;
}

const Wrapper = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.$selected ? color.BROWN_1 : color.GRAY_3)};
  background-color: ${(p) => (p.$selected ? color.BROWN_4 : color.WHITE_1)};
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    border-color: ${color.BROWN_1};
  }
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 6px;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  align-items: center;
`;

const formatDate = (iso: string | null): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const ClozeItem: React.FC<IProps> = ({ cloze, isSelected, handleClick }) => {
  const sourceKeyword = cloze.sourceKeyword;
  const meaningSummary = [cloze.keywordMeaningKo, cloze.keywordMeaningEn, cloze.keywordMeaningJa]
    .filter((m) => !!m && m.trim().length > 0)
    .join(' / ');
  const hasConversation = !!cloze.sourceConversation;
  const publishedLabel = `Published At: ${cloze.publishedAt ? formatDate(cloze.publishedAt) : '-'}`;
  const conversationLabel = hasConversation ? 'Conversation 있음' : 'Conversation 없음';

  return (
    <Wrapper $selected={isSelected} onClick={handleClick}>
      <TopRow>
        <ChipGroup>
          <Chip text={cloze.languageCode} size='tiny' isSelected theme={ETheme.BROWN} />
          <Chip text={cloze.clozeType} size='tiny' isSelected theme={ETheme.GRAY} />
        </ChipGroup>
        <ChipGroup>
          <Chip
            text={conversationLabel}
            size='tiny'
            kind='outlined'
            isSelected
            theme={hasConversation ? ETheme.GREEN : ETheme.RED}
          />
          <Chip
            text={publishedLabel}
            size='tiny'
            isSelected
            theme={cloze.publishedAt ? ETheme.GREEN : ETheme.RED}
          />
        </ChipGroup>
      </TopRow>
      <Body>
        <Chip text={cloze.level} size='tiny' isSelected theme={ETheme.YELLOW} />
        <TextBlock
          text={sourceKeyword || '-'}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true } }}
        />
        {meaningSummary && (
          <TextBlock
            text={meaningSummary}
            design={{ text: { size: 'small', color: color.BLACK_4, oneline: true } }}
          />
        )}
      </Body>
    </Wrapper>
  );
};

export default ClozeItem;
